// ═══════════════════════════════════════════════════════
// ADMIN — Dashboard de Drift / Rebalanceamento
// Mostra todos os clientes com drift fora do alvo
// ═══════════════════════════════════════════════════════
import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { LIGHT, RADIUS } from "../shared/theme.js";
import { logAudit } from "../supabaseClient.js";
import { fmtBRL } from "../shared/formatters.js";
import { SkeletonTable } from "../shared/Skeletons.jsx";
import EmptyState from "./components/EmptyState.jsx";
import {
  fetchAllClientsDriftOverview,
  checkAndAlertDriftAll,
  DRIFT_STATUS,
  DRIFT_THRESHOLDS,
} from "../services/rebalancing.js";
import { useQuery } from "../shared/hooks.js";

const STATUS_COLOR = {
  [DRIFT_STATUS.OK]: "#16A34A",
  [DRIFT_STATUS.ATENCAO]: "#CA8A04",
  [DRIFT_STATUS.REBALANCEAR]: "#DC2626",
};

function HealthBadge({ score }) {
  const color = score >= 80 ? "#16A34A" : score >= 60 ? "#CA8A04" : "#DC2626";
  const bg = score >= 80 ? "#DCFCE7" : score >= 60 ? "#FEF3C7" : "#FEE2E2";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: RADIUS.xl,
        background: bg,
        color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {score}
    </span>
  );
}
HealthBadge.propTypes = { score: PropTypes.number.isRequired };

function DriftBar({ driftPp, maxDrift, T }) {
  const abs = Math.abs(driftPp);
  const color =
    abs <= DRIFT_THRESHOLDS.ok
      ? "#16A34A"
      : abs <= DRIFT_THRESHOLDS.warning
        ? "#CA8A04"
        : "#DC2626";
  const width = maxDrift > 0 ? (abs / maxDrift) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 100 }}>
      <div
        style={{
          flex: 1,
          height: 6,
          background: T?.border || "#E5E7EB",
          borderRadius: RADIUS.sm,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(width, 100)}%`,
            background: color,
            borderRadius: RADIUS.sm,
          }}
        />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color, minWidth: 36, textAlign: "right" }}>
        {driftPp > 0 ? "+" : ""}
        {driftPp.toFixed(1)}pp
      </span>
    </div>
  );
}
DriftBar.propTypes = {
  driftPp: PropTypes.number.isRequired,
  maxDrift: PropTypes.number.isRequired,
  T: PropTypes.object,
};

export default function AdminDriftDashboard({ T = LIGHT }) {
  const { data: overview, loading, refetch } = useQuery(fetchAllClientsDriftOverview, []);
  const [expandedClient, setExpandedClient] = useState(null);
  const [alerting, setAlerting] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [filter, setFilter] = useState("all"); // all | attention | rebalance

  // Guard: ensure overview is always an array (Supabase may return non-array on error)
  const safeOverview = useMemo(() => (Array.isArray(overview) ? overview : []), [overview]);

  const filtered = useMemo(() => {
    if (safeOverview.length === 0) return [];
    if (filter === "attention") return safeOverview.filter((c) => c.needsRebalancing);
    if (filter === "rebalance") return safeOverview.filter((c) => c.classesRebalancear > 0);
    return safeOverview;
  }, [safeOverview, filter]);

  // KPI cards
  const kpis = useMemo(() => {
    if (safeOverview.length === 0)
      return { total: 0, ok: 0, atencao: 0, rebalancear: 0, avgHealth: 0 };
    const ok = safeOverview.filter((c) => !c.needsRebalancing).length;
    const atencao = safeOverview.filter(
      (c) => c.needsRebalancing && c.classesRebalancear === 0
    ).length;
    const rebalancear = safeOverview.filter((c) => c.classesRebalancear > 0).length;
    const avgHealth = Math.round(
      safeOverview.reduce((s, c) => s + c.healthScore, 0) / safeOverview.length
    );
    return { total: safeOverview.length, ok, atencao, rebalancear, avgHealth };
  }, [safeOverview]);

  const handleSendAlerts = useCallback(async () => {
    setAlerting(true);
    setAlertResult(null);
    try {
      const result = await checkAndAlertDriftAll({ thresholdPp: DRIFT_THRESHOLDS.ok });
      setAlertResult(result);
      await logAudit("drift_alerts_sent", {
        alerts_count: result?.length ?? 0,
        threshold_pp: DRIFT_THRESHOLDS.ok,
      }).catch(() => {});
    } catch {
      setAlertResult([]);
    } finally {
      setAlerting(false);
    }
  }, []);

  const globalMaxDrift = useMemo(
    () => Math.max(...(filtered || []).map((c) => c.maxDrift), 1),
    [filtered]
  );

  if (loading) {
    return <SkeletonTable rows={4} cols={5} />;
  }

  return (
    <div>
      {/* ─── KPI Cards ─── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {[
          { label: "Total clientes", value: kpis.total, color: T.text },
          { label: "Dentro do alvo", value: kpis.ok, color: "#16A34A" },
          { label: "Atenção (5-10pp)", value: kpis.atencao, color: "#CA8A04" },
          { label: "Rebalancear (>10pp)", value: kpis.rebalancear, color: "#DC2626" },
          {
            label: "Saúde média",
            value: `${kpis.avgHealth}/100`,
            color: kpis.avgHealth >= 80 ? "#16A34A" : kpis.avgHealth >= 60 ? "#CA8A04" : "#DC2626",
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            style={{
              background: T.card,
              borderRadius: RADIUS.md,
              padding: "14px 16px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <p
              style={{
                fontSize: 10,
                color: T.muted,
                margin: 0,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {kpi.label}
            </p>
            <p style={{ fontSize: 22, fontWeight: 800, color: kpi.color, margin: "4px 0 0" }}>
              {kpi.value}
            </p>
          </div>
        ))}
      </div>

      {/* ─── Toolbar ─── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { key: "all", label: "Todos" },
            { key: "attention", label: "⚠️ Fora do alvo" },
            { key: "rebalance", label: "🔴 Urgente" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: "6px 14px",
                borderRadius: RADIUS.sm,
                border: `1.5px solid ${filter === f.key ? T.text : T.border}`,
                background: filter === f.key ? T.text : "transparent",
                color: filter === f.key ? "white" : T.sub,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={refetch}
            style={{
              padding: "6px 14px",
              borderRadius: RADIUS.sm,
              border: `1.5px solid ${T.border}`,
              background: "transparent",
              color: T.sub,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            🔄 Atualizar
          </button>
          <button
            onClick={handleSendAlerts}
            disabled={alerting}
            style={{
              padding: "6px 14px",
              borderRadius: RADIUS.sm,
              border: "none",
              background: alerting ? "#9CA3AF" : "#DC2626",
              color: "white",
              fontSize: 12,
              fontWeight: 700,
              cursor: alerting ? "wait" : "pointer",
            }}
          >
            {alerting ? "⏳ Enviando…" : "🔔 Alertar clientes fora do alvo"}
          </button>
        </div>
      </div>

      {alertResult && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: RADIUS.sm,
            background: alertResult.length > 0 ? "#FEF3C7" : "#DCFCE7",
            marginBottom: 14,
            fontSize: 12,
            color: alertResult.length > 0 ? "#92400E" : "#166534",
          }}
        >
          {alertResult.length > 0
            ? `✅ ${alertResult.length} alerta(s) enviado(s): ${alertResult.map((a) => a.clientName).join(", ")}`
            : "✅ Todos os clientes estão dentro do alvo."}
        </div>
      )}

      {/* ─── Client Table ─── */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: T.bg }}>
              {["Cliente", "Código", "Patrimônio", "Saúde", "Maior desvio", "Classes", ""].map(
                (h, idx) => (
                  <th
                    key={h || idx}
                    scope="col"
                    style={{
                      padding: "8px 10px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 700,
                      color: T.sub,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((client) => (
              <React.Fragment key={client.clientId}>
                <tr
                  onClick={() =>
                    setExpandedClient(expandedClient === client.clientId ? null : client.clientId)
                  }
                  style={{
                    borderBottom: `1px solid ${T.border}`,
                    cursor: "pointer",
                    background:
                      expandedClient === client.clientId
                        ? T === LIGHT
                          ? "#F0F4FF"
                          : "rgba(37,99,235,0.1)"
                        : "transparent",
                  }}
                >
                  <td
                    style={{ padding: "10px 10px", fontSize: 13, fontWeight: 600, color: T.text }}
                  >
                    {client.clientName}
                  </td>
                  <td style={{ padding: "10px 10px", fontSize: 12, color: T.muted }}>
                    {client.code}
                  </td>
                  <td
                    style={{ padding: "10px 10px", fontSize: 13, color: T.text, fontWeight: 600 }}
                  >
                    {fmtBRL(client.patrimonio)}
                  </td>
                  <td style={{ padding: "10px 10px" }}>
                    <HealthBadge score={client.healthScore} />
                  </td>
                  <td style={{ padding: "10px 10px", minWidth: 140 }}>
                    <div>
                      <DriftBar driftPp={client.maxDrift} maxDrift={globalMaxDrift} T={T} />
                      <span style={{ fontSize: 10, color: T.muted }}>{client.maxDriftClass}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 10px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {client.classesOk > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: RADIUS.sm,
                            background: "#DCFCE7",
                            color: "#16A34A",
                            fontWeight: 600,
                          }}
                        >
                          {client.classesOk} OK
                        </span>
                      )}
                      {client.classesAtencao > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: RADIUS.sm,
                            background: "#FEF3C7",
                            color: "#CA8A04",
                            fontWeight: 600,
                          }}
                        >
                          {client.classesAtencao} ⚠️
                        </span>
                      )}
                      {client.classesRebalancear > 0 && (
                        <span
                          style={{
                            fontSize: 10,
                            padding: "2px 6px",
                            borderRadius: RADIUS.sm,
                            background: "#FEE2E2",
                            color: "#DC2626",
                            fontWeight: 600,
                          }}
                        >
                          {client.classesRebalancear} 🔴
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "10px 10px", fontSize: 12, color: T.muted }}>
                    {expandedClient === client.clientId ? "▲" : "▼"}
                  </td>
                </tr>
                {expandedClient === client.clientId && (
                  <tr key={`${client.clientId}-detail`}>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <div
                        style={{
                          padding: "12px 20px 16px",
                          background: T === LIGHT ? "#F8FAFC" : "rgba(15,23,42,0.5)",
                        }}
                      >
                        {/* Drift detail table */}
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>
                              {["Classe", "Atual %", "Alvo %", "Desvio", "Status"].map((h) => (
                                <th
                                  key={h}
                                  scope="col"
                                  style={{
                                    padding: "6px 8px",
                                    textAlign: "left",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: T.muted,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {client.driftDetails.map((d) => (
                              <tr key={d.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: T.text,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: RADIUS.full,
                                      background: d.color,
                                      display: "inline-block",
                                      marginRight: 6,
                                    }}
                                  />
                                  {d.label}
                                </td>
                                <td style={{ padding: "6px 8px", fontSize: 12, color: T.text }}>
                                  {d.current.toFixed(1)}%
                                </td>
                                <td style={{ padding: "6px 8px", fontSize: 12, color: T.sub }}>
                                  {d.target.toFixed(1)}%
                                </td>
                                <td
                                  style={{
                                    padding: "6px 8px",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color:
                                      d.driftPp > 0
                                        ? "#DC2626"
                                        : d.driftPp < 0
                                          ? "#2563EB"
                                          : T.muted,
                                  }}
                                >
                                  {d.driftPp > 0 ? "+" : ""}
                                  {d.driftPp.toFixed(1)}pp
                                </td>
                                <td style={{ padding: "6px 8px" }}>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: STATUS_COLOR[d.status] || T.muted,
                                    }}
                                  >
                                    {d.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Trade suggestions */}
                        {client.trades.length > 0 && (
                          <div
                            style={{
                              marginTop: 12,
                              padding: "10px 14px",
                              borderRadius: RADIUS.sm,
                              background: T === LIGHT ? "#EFF6FF" : "rgba(37,99,235,0.1)",
                              borderLeft: "3px solid #2563EB",
                            }}
                          >
                            <p
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: T === LIGHT ? "#1E40AF" : "#7DD3FC",
                                margin: "0 0 6px",
                              }}
                            >
                              Sugestões de rebalanceamento:
                            </p>
                            {client.trades.map((t) => (
                              <p
                                key={t.id}
                                style={{
                                  fontSize: 12,
                                  color: T === LIGHT ? "#1E40AF" : "#7DD3FC",
                                  margin: "2px 0",
                                }}
                              >
                                {t.action === "Comprar" ? "🟢" : "🔴"} <strong>{t.action}</strong>{" "}
                                {t.label} — R${" "}
                                {t.amount.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={filter === "all" ? "📊" : "✅"}
                    title={
                      filter === "all"
                        ? "Nenhum dado de drift disponível"
                        : "Nenhum cliente fora do alvo"
                    }
                    subtitle={
                      filter === "all"
                        ? "Importe snapshots para calcular desvios de alocação."
                        : "Todos os clientes estão dentro do alvo neste filtro."
                    }
                    T={T}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

AdminDriftDashboard.propTypes = {
  T: PropTypes.object,
};
